import { useState } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  BookOpen,
  Clock,
  Star,
  ChevronRight,
  ArrowLeft,
  Layers,
  Play,
  Loader2,
  AlertCircle,
  Target,
  CreditCard,
  Sparkles,
  CheckCircle2
} from 'lucide-react'
import { contentApi } from '@/api/content'
import { commerceApi, formatPrice, getBillingPeriodLabel } from '@/api/commerce'
import { subscriptionApi } from '@/api/subscription'
import { useAuthStore } from '@/store/authStore'
import { CurriculumSidebar } from '@/components/learn/CurriculumSidebar'
import { Menu, X } from 'lucide-react'

// 학습 시간을 시간 단위로 변환
function formatDuration(minutes: number | null | undefined): string {
  if (!minutes) return '-'
  if (minutes < 60) return `${minutes}분`
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return mins > 0 ? `${hours}시간 ${mins}분` : `${hours}시간`
}

export function CurriculumDetailPage() {
  const { curriculumId } = useParams<{ curriculumId: string }>()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuthStore()
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false)

  const {
    data: curriculum,
    isLoading,
    error
  } = useQuery({
    queryKey: ['curriculum', curriculumId],
    queryFn: () => contentApi.getCurriculumDetail(Number(curriculumId)),
    enabled: !!curriculumId,
    staleTime: 5 * 60 * 1000,
  })

  // 상품 정보 조회
  const { data: productInfo, isLoading: isLoadingProduct } = useQuery({
    queryKey: ['curriculum-product', curriculumId],
    queryFn: () => commerceApi.getProductByCurriculum(Number(curriculumId)),
    enabled: !!curriculumId,
    staleTime: 5 * 60 * 1000,
  })

  // 내 구독 정보 조회
  const { data: mySubscriptions = [] } = useQuery({
    queryKey: ['my-subscriptions'],
    queryFn: () => subscriptionApi.getMySubscriptions(),
    enabled: isAuthenticated,
    staleTime: 60 * 1000,
  })

  // 이 커리큘럼에 대한 활성 구독이 있는지 확인
  const hasActiveSubscription = mySubscriptions.some(
    sub => sub.curriculumContentId === Number(curriculumId) && sub.status === 'active'
  )

  const handleSubscribe = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/learn/curriculum/${curriculumId}` } })
      return
    }

    if (productInfo?.productId) {
      navigate(`/checkout/${productInfo.productId}`)
    }
  }

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2 className="w-10 h-10 text-primary-600 animate-spin mb-4" />
        <p className="text-gray-600">콘텐츠를 불러오는 중...</p>
      </div>
    )
  }

  // 에러 상태
  if (error || !curriculum) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
        <p className="text-gray-900 font-medium mb-2">커리큘럼을 찾을 수 없습니다</p>
        <Link to="/learn" className="text-primary-600 hover:underline">
          목록으로 돌아가기
        </Link>
      </div>
    )
  }

  const totalLessons = curriculum.courses.reduce((sum, c) => sum + c.lessonCount, 0)
  const defaultPricing = productInfo?.pricingOptions.find(p => p.isDefault) || productInfo?.pricingOptions[0]

  // useState import needed at top? I'll check if it's already there. 
  // It's not imported in the file view I saw. I need to add it.

  return (
    <div className="flex h-[calc(100vh-64px)] bg-gray-50 overflow-hidden">
      {/* Desktop Sidebar */}
      <div className="hidden lg:block w-80 h-full border-r border-gray-200 bg-white shrink-0">
        <CurriculumSidebar
          curriculumId={Number(curriculumId)}
        />
      </div>

      {/* Mobile Sidebar */}
      {isMobileSidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setIsMobileSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-80 bg-white shadow-xl z-50 animate-in slide-in-from-left duration-200">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="font-bold text-lg">목차</span>
              <button
                onClick={() => setIsMobileSidebarOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <CurriculumSidebar
              curriculumId={Number(curriculumId)}
              className="border-none w-full"
            />
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto custom-scrollbar relative">
        {/* Mobile Header (Sidebar Toggle) */}
        <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between shadow-xs">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setIsMobileSidebarOpen(true)}
              className="p-2 -ml-2 hover:bg-gray-100 rounded-lg"
            >
              <Menu className="w-5 h-5 text-gray-600" />
            </button>
            <h1 className="font-bold text-gray-900 truncate max-w-[200px]">
              {curriculum.title}
            </h1>
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-4 md:p-8 space-y-8">
          {/* Back Button */}
          <Link
            to="/learn"
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>학습 목록으로</span>
          </Link>

          {/* Header */}
          <div className="card p-6 bg-linear-to-r from-primary-50 to-primary-100">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Info */}
              <div className="flex-1">
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                  {curriculum.title}
                </h1>
                <p className="text-gray-700 mb-4">
                  {curriculum.description || '수학의 핵심 개념을 체계적으로 학습합니다.'}
                </p>

                {/* Stats */}
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2 bg-white/70 px-3 py-1.5 rounded-full">
                    <BookOpen className="w-4 h-4 text-primary-600" />
                    <span className="text-gray-700">{curriculum.courses.length}개 코스</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/70 px-3 py-1.5 rounded-full">
                    <Play className="w-4 h-4 text-green-600" />
                    <span className="text-gray-700">{totalLessons}개 강의</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/70 px-3 py-1.5 rounded-full">
                    <Clock className="w-4 h-4 text-yellow-600" />
                    <span className="text-gray-700">{formatDuration(curriculum.estimatedDurationMinutes)}</span>
                  </div>
                  {curriculum.starRating && (
                    <div className="flex items-center gap-2 bg-white/70 px-3 py-1.5 rounded-full">
                      <Star className="w-4 h-4 text-yellow-500 fill-current" />
                      <span className="text-gray-700">{curriculum.starRating}점</span>
                    </div>
                  )}
                </div>

                {/* Learning Objectives */}
                {curriculum.learningObjectives && (
                  <div className="mt-4 p-4 bg-white/50 rounded-lg">
                    <h3 className="font-medium text-gray-900 flex items-center gap-2 mb-2">
                      <Target className="w-4 h-4 text-primary-600" />
                      학습 목표
                    </h3>
                    <p className="text-sm text-gray-700">{curriculum.learningObjectives}</p>
                  </div>
                )}
              </div>

              {/* Subscription Card */}
              <div className="lg:w-80 shrink-0">
                <div className="bg-white rounded-xl p-6 shadow-lg border border-primary-200">
                  {isLoadingProduct ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
                    </div>
                  ) : hasActiveSubscription ? (
                    <>
                      {/* Already Subscribed */}
                      <div className="text-center mb-4">
                        <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-3">
                          <CheckCircle2 className="w-8 h-8 text-green-600" />
                        </div>
                        <span className="text-xl font-bold text-green-600">구독 중</span>
                        <p className="text-sm text-gray-500 mt-1">지금 바로 학습을 시작하세요!</p>
                      </div>

                      {curriculum.courses.length > 0 && (
                        <Link
                          to={`/learn/course/${curriculum.courses[0].id}`}
                          className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2"
                        >
                          <Play className="w-5 h-5" />
                          학습 시작
                        </Link>
                      )}

                      {/* Benefits */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                            모든 강의 무제한 시청
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                            진도 관리 및 기록
                          </li>
                        </ul>
                      </div>
                    </>
                  ) : productInfo?.hasProduct && defaultPricing ? (
                    <>
                      {/* Price Display */}
                      <div className="text-center mb-4">
                        <div className="flex items-center justify-center gap-2 mb-1">
                          <Sparkles className="w-5 h-5 text-yellow-500" />
                          <span className="text-sm font-medium text-primary-600">
                            {getBillingPeriodLabel(defaultPricing.billingPeriod)} 구독
                          </span>
                        </div>
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-3xl font-bold text-gray-900">
                            {formatPrice(defaultPricing.price, defaultPricing.currency)}
                          </span>
                          {defaultPricing.originalPrice && (
                            <span className="text-lg text-gray-400 line-through">
                              {formatPrice(defaultPricing.originalPrice, defaultPricing.currency)}
                            </span>
                          )}
                        </div>
                        {defaultPricing.discountPercentage && (
                          <span className="inline-block mt-2 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-sm">
                            {defaultPricing.discountPercentage}% 할인
                          </span>
                        )}
                      </div>

                      {/* Subscribe Button */}
                      <button
                        onClick={handleSubscribe}
                        className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2 mb-3"
                      >
                        <CreditCard className="w-5 h-5" />
                        구독하기
                      </button>

                      {/* Other Pricing Options */}
                      {productInfo.pricingOptions.length > 1 && (
                        <button
                          onClick={handleSubscribe}
                          className="text-sm text-primary-600 hover:underline w-full text-center"
                        >
                          다른 플랜 보기
                        </button>
                      )}

                      {/* Benefits */}
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <ul className="space-y-2 text-sm text-gray-600">
                          <li className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                            모든 강의 무제한 시청
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                            진도 관리 및 기록
                          </li>
                          <li className="flex items-center gap-2">
                            <span className="w-4 h-4 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
                            언제든 취소 가능
                          </li>
                        </ul>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Free Access or No Product */}
                      <div className="text-center mb-4">
                        <span className="text-2xl font-bold text-gray-900">무료</span>
                        <p className="text-sm text-gray-500 mt-1">지금 바로 시작하세요</p>
                      </div>

                      {curriculum.courses.length > 0 && (
                        <Link
                          to={`/learn/course/${curriculum.courses[0].id}`}
                          className="btn-primary w-full py-4 text-lg flex items-center justify-center gap-2"
                        >
                          <Play className="w-5 h-5" />
                          학습 시작
                        </Link>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Courses List */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Layers className="w-5 h-5 text-primary-600" />
              코스 목록
            </h2>

            <div className="space-y-4">
              {curriculum.courses.map((course, index) => (
                <Link
                  key={course.id}
                  to={`/learn/course/${course.id}`}
                  className="card p-5 hover:shadow-lg transition-all flex items-center gap-4 group"
                >
                  {/* Course Number */}
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center text-primary-600 font-bold text-lg shrink-0">
                    {index + 1}
                  </div>

                  {/* Course Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors truncate">
                      {course.title}
                    </h3>
                    <p className="text-sm text-gray-500 truncate mt-1">
                      {course.description || course.shortDescription || ''}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" />
                        {course.moduleCount}개 모듈
                      </span>
                      <span className="flex items-center gap-1">
                        <Play className="w-3.5 h-3.5" />
                        {course.lessonCount}개 강의
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDuration(course.estimatedDurationMinutes)}
                      </span>
                    </div>
                  </div>

                  {/* Arrow */}
                  <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-primary-600 transition-colors shrink-0" />
                </Link>
              ))}
            </div>
          </section>

          {/* Empty State */}
          {curriculum.courses.length === 0 && (
            <div className="card p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BookOpen className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                등록된 코스가 없습니다
              </h3>
              <p className="text-gray-500">
                콘텐츠가 준비 중입니다. 잠시 후 다시 확인해주세요.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

